import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useRestaurant } from "@/rc/hooks/restaurant/use-restaurant";

/** Convert a hex color (#RRGGBB) to HSL string "H S% L%" for CSS variables. */
function hexToHsl(hex: string): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** CSS custom properties managed by FontProvider — cleaned up on restaurant switch. */
const MANAGED_PROPS = [
  "--font-heading",
  "--font-body",
  "--primary",
  "--secondary",
  "--ring",
  "--sidebar-primary",
  "--sidebar-ring",
  "--sidebar-background",
  "--background",
  "--dynamic-bg-color",
  "--foreground",
  "--card-foreground",
  "--popover-foreground",
  "--muted",
  "--border",
  "--input",
  "--card",
  "--popover",
] as const;

function applyHsl(
  root: HTMLElement,
  prop: string,
  hex: string | null | undefined
) {
  if (!hex) return;
  const hsl = hexToHsl(hex);
  if (hsl) root.style.setProperty(prop, hsl);
}

/**
 * Loads Google Fonts and applies heading/body fonts + colors
 * from the active `restaurants` row to CSS custom properties.
 * (Fonts and theme colors live on `restaurants`, not `restaurant_settings`.)
 * Cleans up properties when unmounted (restaurant switch).
 */
export function FontProvider({ children }: { children: React.ReactNode }) {
  const { data: restaurant } = useRestaurant();

  useEffect(() => {
    const root = document.documentElement;

    if (!restaurant) return;

    const headingFont = restaurant.heading_font || "Geist";
    const bodyFont = restaurant.body_font || "Geist";

    // Load fonts from Google Fonts
    const families = new Set([headingFont, bodyFont]);
    const linkId = "dynamic-google-fonts";
    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    const url = `https://fonts.googleapis.com/css2?${[...families]
      .map(
        (f) => `family=${f.replace(/ /g, "+")}:wght@300;400;500;600;700;800;900`
      )
      .join("&")}&display=swap`;

    if (!link) {
      link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = url;

    // Apply CSS custom properties
    root.style.setProperty("--font-heading", `'${headingFont}', sans-serif`);
    root.style.setProperty("--font-body", `'${bodyFont}', sans-serif`);

    applyHsl(root, "--secondary", restaurant.primary_color);
    if (restaurant.accent_color) {
      const hsl = hexToHsl(restaurant.accent_color);
      if (hsl) {
        root.style.setProperty("--primary", hsl);
        root.style.setProperty("--ring", hsl);
        root.style.setProperty("--sidebar-primary", hsl);
        root.style.setProperty("--sidebar-ring", hsl);
      }
    }

    if (restaurant.background_color) {
      applyHsl(root, "--background", restaurant.background_color);
      root.style.setProperty("--dynamic-bg-color", restaurant.background_color);
    }

    if (restaurant.foreground_color) {
      const hsl = hexToHsl(restaurant.foreground_color);
      if (hsl) {
        root.style.setProperty("--foreground", hsl);
        root.style.setProperty("--card-foreground", hsl);
        root.style.setProperty("--popover-foreground", hsl);
      }
    }

    if (restaurant.sidebar_color) {
      applyHsl(root, "--sidebar-background", restaurant.sidebar_color);
    } else if (restaurant.primary_color) {
      applyHsl(root, "--sidebar-background", restaurant.primary_color);
    }

    applyHsl(root, "--muted", restaurant.muted_color);

    if (restaurant.border_color) {
      const hsl = hexToHsl(restaurant.border_color);
      if (hsl) {
        root.style.setProperty("--border", hsl);
        root.style.setProperty("--input", hsl);
      }
    }

    if (restaurant.card_color) {
      const hsl = hexToHsl(restaurant.card_color);
      if (hsl) {
        root.style.setProperty("--card", hsl);
        root.style.setProperty("--popover", hsl);
      }
    }

    // Cleanup: remove all managed properties when restaurant changes or unmounts
    return () => {
      for (const prop of MANAGED_PROPS) {
        root.style.removeProperty(prop);
      }
    };
  }, [restaurant]);

  return <>{children}</>;
}
